/**
 * Manager Registration Page
 * Supports both passkey and magic link registration
 */

import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { startRegistration } from '@simplewebauthn/browser';
import {
  managerRegister,
  managerRegisterVerify,
  managerSession,
  managerRequestRegistrationMagicLink,
  managerRequestRegistrationEmailCode,
  ApiClientError,
} from '../../lib/api';
import { useAuth } from '../../hooks/useAuth';
import { createLogger, POLICY_VERSIONS, type DocumentPolicyType } from '@resida/shared';
import { PolicyModal } from '../../components/PolicyModal';
import { Button } from '../../components/ui/button';

const logger = createLogger('landlord:pages:auth:RegisterPage');

type RegistrationStep = 'email' | 'passkey' | 'magicLinkSent' | 'success';
type AuthMethod = 'passkey' | 'magicLink' | 'emailCode';

export function RegisterPage() {
  const navigate = useNavigate();
  const { setSession } = useAuth();

  const [email, setEmail] = useState('');
  const [authMethod, setAuthMethod] = useState<AuthMethod>('passkey');
  const [step, setStep] = useState<RegistrationStep>('email');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [consentChecked, setConsentChecked] = useState(false);
  const [openPolicy, setOpenPolicy] = useState<DocumentPolicyType | null>(null);

  const policiesPayload = [
    { policyType: 'platform_terms', policyVersion: POLICY_VERSIONS.platform_terms },
    { policyType: 'privacy_policy', policyVersion: POLICY_VERSIONS.privacy_policy },
    { policyType: 'landlord_supplement', policyVersion: POLICY_VERSIONS.landlord_supplement },
  ];

  /**
   * Handle passkey registration
   */
  async function handlePasskeyRegister(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      // Step 1: Get registration options from server
      const { options, managerId } = await managerRegister(email);

      setStep('passkey');

      // Step 2: Start WebAuthn registration
      const credential = await startRegistration({ optionsJSON: options });

      // Step 3: Verify registration with server
      await managerRegisterVerify(managerId, credential, policiesPayload);

      // Step 4: Fetch session and update auth context
      const session = await managerSession();
      if (session) {
        setSession(session);
      } else {
        // Session not established - show error
        throw new Error('Failed to establish session. Please try logging in.');
      }

      setStep('success');

      // Step 5: Redirect to dashboard
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    } catch (err) {
      logger.error('Passkey registration error:', err);
      handleError(err);
      setStep('email');
    } finally {
      setIsLoading(false);
    }
  }

  /**
   * Handle email code registration request
   */
  async function handleEmailCodeRegister(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await managerRequestRegistrationEmailCode(email);
      navigate('/auth/email-code/verify', { state: { email, purpose: 'register', policies: policiesPayload } });
    } catch (err) {
      logger.error('Email code registration error:', err);
      handleError(err);
    } finally {
      setIsLoading(false);
    }
  }

  /**
   * Handle magic link registration request
   */
  async function handleMagicLinkRegister(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await managerRequestRegistrationMagicLink(email, policiesPayload);
      setStep('magicLinkSent');
    } catch (err) {
      logger.error('Magic link registration error:', err);
      handleError(err);
    } finally {
      setIsLoading(false);
    }
  }

  /**
   * Handle and display errors
   */
  function handleError(err: unknown) {
    if (err instanceof ApiClientError) {
      if (err.code === 'EMAIL_EXISTS') {
        setError('An account with this email already exists. Please sign in instead.');
      } else if (err.code === 'RATE_LIMITED') {
        setError('Too many requests. Please wait a few minutes and try again.');
      } else if (err.code === 'INVALID_EMAIL') {
        setError('Please enter a valid email address.');
      } else {
        setError(err.message);
      }
    } else if (err instanceof Error) {
      // WebAuthn errors
      if (err.name === 'NotAllowedError') {
        setError('Registration was cancelled. Please try again.');
      } else if (err.name === 'InvalidStateError') {
        setError('A passkey for this device already exists.');
      } else if (err.name === 'NotSupportedError') {
        setError('Your browser or device does not support passkeys.');
      } else {
        setError(err.message || 'Registration failed. Please try again.');
      }
    } else {
      setError('An unexpected error occurred. Please try again.');
    }
  }

  /**
   * Validate email format
   */
  function isValidEmail(value: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  return (
    <div className="min-h-screen bg-muted/50 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        <div className="bg-card rounded-xl shadow-sm border border-border p-6 md:p-8">
          <h1 className="text-2xl font-semibold text-foreground mb-2 text-center">
            Create Your Account
          </h1>
          <p className="text-sm text-muted-foreground mb-6 text-center">
            Welcome to Resida Landlord Portal
          </p>

          {error && (
            <div className="text-sm text-destructive p-3 bg-destructive/5 border border-destructive/20 rounded-md mb-4">
              {error}
            </div>
          )}

          {step === 'magicLinkSent' ? (
            <div className="text-center pt-5">
              <div className="w-16 h-16 mx-auto mb-4 bg-success text-success-foreground rounded-full flex items-center justify-center text-3xl">
                ✓
              </div>
              <h2 className="text-xl font-semibold text-foreground mb-3">
                Check Your Email
              </h2>
              <p className="text-sm text-foreground mb-2">
                We've sent a magic link to <strong>{email}</strong>
              </p>
              <p className="text-sm text-muted-foreground mb-2">
                Click the link in the email to complete your registration.
              </p>
              <p className="text-xs text-muted-foreground mb-6">
                The link will expire in 15 minutes.
              </p>
              <Button
                variant="outline"
                onClick={() => {
                  setStep('email');
                  setEmail('');
                }}
                className="w-full min-h-[44px]"
              >
                Back to Registration
              </Button>
            </div>
          ) : step === 'success' ? (
            <div className="text-center pt-5">
              <div className="w-12 h-12 mx-auto mb-4 bg-success text-success-foreground rounded-full flex items-center justify-center text-2xl">
                ✓
              </div>
              <p className="text-sm text-foreground">Registration successful!</p>
              <p className="text-xs text-muted-foreground mt-2">
                Redirecting to dashboard...
              </p>
            </div>
          ) : (
            <form
              onSubmit={authMethod === 'passkey' ? handlePasskeyRegister : authMethod === 'magicLink' ? handleMagicLinkRegister : handleEmailCodeRegister}
              className="flex flex-col gap-4"
            >
              <div className="flex flex-col gap-1.5">
                <label htmlFor="email" className="text-sm font-medium text-muted-foreground">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  disabled={isLoading}
                  required
                  className="w-full min-h-[44px] px-3 py-2.5 text-sm border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring disabled:opacity-60"
                />
              </div>

              <div className="flex flex-col gap-3">
                <label className="text-sm font-medium text-muted-foreground">Choose registration method:</label>

                <label
                  className={`flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    authMethod === 'passkey'
                      ? 'border-primary bg-primary/5'
                      : 'border-border bg-transparent'
                  }`}
                >
                  <input
                    type="radio"
                    name="authMethod"
                    value="passkey"
                    checked={authMethod === 'passkey'}
                    onChange={(e) => setAuthMethod(e.target.value as AuthMethod)}
                    disabled={isLoading}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <div className="font-semibold text-sm mb-1">
                      Create with Passkey
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Fast, secure biometric authentication (recommended)
                    </div>
                  </div>
                </label>

                <label
                  className={`flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    authMethod === 'magicLink'
                      ? 'border-primary bg-primary/5'
                      : 'border-border bg-transparent'
                  }`}
                >
                  <input
                    type="radio"
                    name="authMethod"
                    value="magicLink"
                    checked={authMethod === 'magicLink'}
                    onChange={(e) => setAuthMethod(e.target.value as AuthMethod)}
                    disabled={isLoading}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <div className="font-semibold text-sm mb-1">
                      Send me a magic link
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Email-based registration (works on any device)
                    </div>
                  </div>
                </label>

                <label
                  className={`flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    authMethod === 'emailCode'
                      ? 'border-primary bg-primary/5'
                      : 'border-border bg-transparent'
                  }`}
                >
                  <input
                    type="radio"
                    name="authMethod"
                    value="emailCode"
                    checked={authMethod === 'emailCode'}
                    onChange={(e) => setAuthMethod(e.target.value as AuthMethod)}
                    disabled={isLoading}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <div className="font-semibold text-sm mb-1">
                      Email me a code
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Enter a one-time code from your email
                    </div>
                  </div>
                </label>
              </div>

              <div className="flex items-start gap-2" data-testid="consent-checkbox-container">
                <input
                  type="checkbox"
                  id="consent"
                  checked={consentChecked}
                  onChange={(e) => setConsentChecked(e.target.checked)}
                  className="mt-1"
                  data-testid="consent-checkbox"
                  aria-required="true"
                />
                <label htmlFor="consent" className="text-sm text-foreground">
                  I agree to the{' '}
                  <button
                    type="button"
                    onClick={() => setOpenPolicy('platform_terms')}
                    className="text-primary underline"
                    data-testid="consent-link-platform-terms"
                  >
                    Terms of Use
                  </button>
                  {' '}and{' '}
                  <button
                    type="button"
                    onClick={() => setOpenPolicy('privacy_policy')}
                    className="text-primary underline"
                    data-testid="consent-link-privacy-policy"
                  >
                    Privacy Policy
                  </button>
                  , and the{' '}
                  <button
                    type="button"
                    onClick={() => setOpenPolicy('landlord_supplement')}
                    className="text-primary underline"
                    data-testid="consent-link-landlord-supplement"
                  >
                    Landlord Supplemental Terms
                  </button>
                  .
                </label>
              </div>

              {openPolicy && (
                <PolicyModal
                  policyType={openPolicy}
                  isOpen={true}
                  onClose={() => setOpenPolicy(null)}
                />
              )}

              <Button
                type="submit"
                disabled={isLoading || !isValidEmail(email) || !consentChecked}
                className="w-full min-h-[44px]"
                data-testid={authMethod === 'emailCode' ? 'request-email-code-btn' : 'register-submit'}
              >
                {isLoading
                  ? authMethod === 'passkey'
                    ? step === 'passkey'
                      ? 'Creating passkey...'
                      : 'Processing...'
                    : authMethod === 'emailCode'
                      ? 'Sending Code...'
                      : 'Sending Link...'
                  : authMethod === 'passkey'
                    ? 'Create Account with Passkey'
                    : authMethod === 'emailCode'
                      ? 'Email me a code'
                      : 'Send Magic Link'}
              </Button>
            </form>
          )}

          {step !== 'success' && step !== 'magicLinkSent' && (
            <p className="mt-6 text-sm text-muted-foreground text-center">
              Already have an account?{' '}
              <Link
                to="/login"
                className="text-primary hover:text-primary/80 no-underline"
              >
                Sign in
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default RegisterPage;
